import React from "react";
import { useTranslate } from "@refinedev/core";
import { Col, Row, Typography } from "antd";
import type { AddonActivation } from "types/";
import { CompactStatCard, type CompactStatCardProps } from "../CompactStatCard";
import { formatPct } from "../utils/formatPct";
import { percent, type AddonKey } from "./utils/metrics";

const T_PREFIX = "media.projects.analytics.addons";

export type AddonStat = { key: string } & Pick<
  CompactStatCardProps,
  "label" | "metric" | "type" | "subtitle" | "prefix" | "valueColor"
>;

export type AddonPanelHeaderProps = {
  addon: AddonKey;
  activation: AddonActivation;
  projectSessions: number;
  /** Shown after the add-on name, e.g. its number of items. */
  count: string | null;
  /** Cards after the opened, used and completed ones. */
  stats: AddonStat[];
};

/** The add-on name, then the opened, used and completed cards every add-on panel opens with. */
export const AddonPanelHeader: React.FC<AddonPanelHeaderProps> = ({
  addon,
  activation,
  projectSessions,
  count,
  stats,
}) => {
  const t = useTranslate();
  const funnel: AddonStat[] = [
    {
      key: "opened",
      label: t(`${T_PREFIX}.stat.opened`),
      metric: activation.sessions_opened,
      subtitle: t(`${T_PREFIX}.stat.openedHint`, {
        pct: formatPct(percent(activation.sessions_opened, projectSessions)),
      }),
    },
    {
      key: "interacted",
      label: t(`${T_PREFIX}.stat.interacted`),
      metric: activation.sessions_interacted,
      subtitle: t(`${T_PREFIX}.usedRule.${addon}`),
    },
    {
      key: "completed",
      label: t(`${T_PREFIX}.stat.completed`),
      metric: activation.sessions_completed,
      subtitle: t(`${T_PREFIX}.rule.${addon}`),
    },
  ];

  return (
    <>
      <Typography.Title level={4} style={{ margin: 0, fontWeight: 500 }}>
        {t(`${T_PREFIX}.names.${addon}`)}{" "}
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          {count}
        </Typography.Text>
      </Typography.Title>

      <Row gutter={[8, 8]}>
        {[...funnel, ...stats].map(({ key, ...stat }) => (
          <Col key={key} xs={24} sm={12} lg={8} xl={4}>
            <CompactStatCard {...stat} flat />
          </Col>
        ))}
      </Row>
    </>
  );
};
